import uuid from 'uuid/v4'
import _ from 'lodash'
import Vue from 'vue'
import quoteService from '@/services/quote'
import Promise from 'bluebird'
import dropdowns from '@/data/dropdowns.json'

export default {
  namespaced: true,
  state: {
    id: uuid(),
    items: [],
    profile: null,
    notes: '',
    promoCode: '',
    language: ''
  },
  getters: {
    id: state => state.id,
    items: state => state.items,
    profile: state => state.profile,
    itemsLength: state => state.items.length,
    getItemIndex: (state, getters) => id => _.findIndex(getters.items, item => item.id === id),
    getItem: (state, getters) => id => {
      const index = getters.getItemIndex(id)
      return state.items[index]
    },
    dropdowns: () => {
      return dropdowns
    },
    notes: state => state.notes,
    promoCode: state => state.promoCode,
    language: state => state.language
  },
  mutations: {
    insertItem (state, { index, id, file, filename, extension, ...info }) {
      const item = {
        id,
        file,
        filename,
        extension,
        unitOfMeasure: info.unitOfMeasure || 'millimeters',
        solutionType: info.solutionType || 'economical',
        materialType: info.materialType || 'economical',
        postProcessOptions: info.postProcessOptions || 'none',
        finishLevel: info.finishLevel || 'strip-and-ship',
        paintType: info.paintType || 'no-paint',
        paintColor: info.paintColor || 'none',
        quantity: info.quantity || 1,
        leadTime: info.leadTime || 'standard',
        notes: info.notes || ''
      }

      if (_.isInteger(index)) {
        state.items.splice(index, 0, item)
      } else {
        state.items.push(item)
      }
    },
    removeItem (state, { index }) { state.items.splice(index, 1) },
    resetQuoteId (state) { state.id = uuid() },
    updateProfile (state, { value }) { state.profile = value },
    updateItemProperty (state, { index, key, value }) { Vue.set(state.items[index], key, value) },
    updateNotes (state, { value }) { state.notes = value },
    updatePromoCode (state, { value }) { state.promoCode = value },
    updateLanguage (state, { value }) { state.language = value },
    resetAll (state) {
      state.items = []
      state.notes = ''
      state.promoCode = ''
    }
  },
  actions: {
    deleteItem ({ commit, getters }, { id }) {
      const index = getters.getItemIndex(id)
      commit('removeItem', { index })
    },
    async fetchProfile ({ commit, getters }) {
      const response = await quoteService.fetchProfile()
      commit('updateProfile', { value: response.data.content })
    },
    async completeProfile ({ commit, getters }, args) {
      await quoteService.completeProfile({
        ...args
      })
      commit('updateProfile', { value: args })
    },
    createItem ({ commit }, args) {
      const id = uuid()
      const fileNameParts = args.file.name.split('.')
      const extension = [...fileNameParts].pop()
      const name = [...fileNameParts].slice(0, fileNameParts.length - 1).join('.')
      const filename = `${name}-${id.substr(0, 8)}.${extension}`

      commit('insertItem', { ...args, id, filename, extension })
    },
    updateItem ({ commit, getters }, { id, ...keyValues }) {
      const index = getters.getItemIndex(id)

      if (index === -1) return

      if (keyValues.file) {
        const fileNameParts = keyValues.file.name.split('.')
        const extension = [...fileNameParts].pop()
        const name = [...fileNameParts].slice(0, fileNameParts.length - 1).join('.')
        const filename = `${name}-${id.substr(0, 8)}.${extension}`

        keyValues.extension = extension
        keyValues.filename = filename
      }

      for (const key in keyValues) {
        commit('updateItemProperty', { index, key, value: keyValues[key] })
      }
    },
    duplicateItem ({ commit, getters }, { id }) {
      const index = getters.getItemIndex(id)
      const item = { ...getters.items[index] } // copy of object
      const copyIndex = index + 1

      commit('insertItem', {
        ...item,
        id: uuid(),
        index: copyIndex
      })
    },
    copyPreviousItemConfig ({ dispatch, getters }, { id }) {
      const index = getters.getItemIndex(id)
      const previousIndex = index - 1
      const previousItem = { ...getters.items[previousIndex] }

      dispatch('updateItem', {
        ..._.pick(previousItem, ['solutionType']),
        id
      })

      dispatch('updateItem', {
        ..._.omit(previousItem, ['file', 'extension', 'filename', 'solutionType']),
        id
      })
    },
    updateNotes ({ commit }, value) {
      commit('updateNotes', { value })
    },
    updatePromoCode ({ commit }, value) {
      commit('updatePromoCode', { value })
    },
    updateLanguage ({ commit }, value) {
      commit('updateLanguage', { value })
    },
    async sendQuoteRequest ({ getters, dispatch }) {
      const fileList = []
      try {
        // Upload every items file
        await Promise.map(getters.items, async item => {
          if (fileList.indexOf(item.filename) !== -1) { return Promise.resolve() } // Don't upload the same file twice
          fileList.push(item.filename) // Remember filename to not upload it twice
          dispatch('startTask', { id: item.filename }, { root: true })
          await quoteService.uploadFile({
            quoteId: getters.id,
            file: item.file,
            filename: item.filename
          }, (progressEvent) => {
            dispatch('updateTaskProgress', {
              id: item.filename,
              progress: progressEvent.loaded / progressEvent.total
            }, { root: true })
          })
          return dispatch('endTask', { id: item.filename }, { root: true })
        })

        dispatch('startTask', { id: 'sendQuote' }, { root: true })
        // Send the quote
        await quoteService.sendQuote({
          quoteId: getters.id,
          items: getters.items,
          notes: getters.notes,
          promoCode: getters.promoCode,
          language: getters.language
        })

        return dispatch('endTask', { id: 'sendQuote' }, { root: true })
      } catch (error) {
        dispatch('clearTasks', null, { root: true })
        throw error
      }
    },
    resetQuoteId ({ commit }) { commit('resetQuoteId') },
    resetAll ({ commit }) { commit('resetAll') }
  }
}
